import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
} from '@mui/material';
import {keepPreviousData} from '@tanstack/react-query';
import {useDebounce} from '@uidotdev/usehooks';
import React, {Suspense, useCallback, useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';

import FeatureTable from './FeaturesTable.jsx';
import {setCollectionVisibility} from '@actions/collections';
import {fitExtent} from '@actions/map';
import {notify} from '@actions/notifications';
import {deselectFeatureRef} from '@actions/selected-features';
import {useGetProductsCount} from '@api/products';
import {useGetReservations} from '@api/quotaReservations';
import {trackEvent} from '@app/analytics';
import {useGetCollection} from '@app/api/collections';
import DrawerHeader from '@app/components/common/DrawerHeader';
import FilterTextField from '@app/components/common/FilterTextField';
import {MODALS, TOUR_STAGES} from '@app/constants';
import useCollectionParams from '@app/hooks/useCollectionParams';
import MoreActionsMenu from '@components/Collections/CollectionActions/MoreActionsMenu';
import ToggleVisibilityButton from '@components/Collections/CollectionActions/ToggleVisibilityButton';
import ZoomToCollectionButton from '@components/Collections/CollectionActions/ZoomToCollectionButton';

const QuotaManagerModal = React.lazy(
  () => import('@app/components/QuotaManager/Modal')
);

const EVENT_NAME = 'Features Panel';

const FeaturesPanel = () => {
  const dispatch = useDispatch();
  const {collectionId, resetCollection, zoom} = useCollectionParams();
  const [showSearch, setShowSearch] = useState(false);
  const [modal, setModal] = useState('');

  const closeModal = useCallback(() => {
    setModal('');
  }, [setModal]);

  const {data: collection, error} = useGetCollection(
    {collectionId},
    {
      enabled: !!collectionId,
    }
  );

  const {data: productsCount} = useGetProductsCount();

  const {data: reservationsData} = useGetReservations(
    {
      collection_id: collectionId,
      state: 'reserved',
    },
    {
      enabled: !!collectionId && productsCount > 0,
      placeholderData: keepPreviousData,
    }
  );

  const [featureSearchValue, setFeatureSearchValue] = useState('');
  const debouncedSearchFilter = useDebounce(featureSearchValue, 250);
  const showQuotaMenu = productsCount > 0;

  useEffect(() => {
    if (error) {
      let errorMessage = `There was an error while loading collection with id ${collectionId}`;

      if (error.response?.status === 403) {
        errorMessage = `You do not have permission to view collection with id ${collectionId}`;
      }

      dispatch(
        notify({
          message: errorMessage,
          severity: 'error',
          persist: true,
        })
      );

      resetCollection();
    }
  }, [collectionId, dispatch, error, resetCollection]);

  useEffect(() => {
    if (collection) {
      dispatch(setCollectionVisibility({collection, visible: true}));
      if (collection.feature_count > 0 && collection.extent && zoom) {
        dispatch(fitExtent(collection.extent?.spatial?.bbox[0]));
      }
    }
  }, [collection, dispatch, zoom]);

  return (
    <>
      <DrawerHeader
        data-tour={TOUR_STAGES.FEATURE_HEADER}
        data-testid="features-panel-header"
        avatar={
          <>
            <Tooltip title="Back to collections">
              <IconButton
                onClick={() => {
                  trackEvent(EVENT_NAME, {
                    action: 'Close features view',
                    collectionId,
                  });
                  setFeatureSearchValue('');
                  resetCollection();
                  dispatch(deselectFeatureRef());
                }}
                size="small"
                sx={{marginRight: 1}}
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Divider orientation="vertical" flexItem />
          </>
        }
        title={collection?.title}
        action={
          collection ? (
            <Stack spacing={0.5} direction="row">
              <ToggleVisibilityButton
                collection={collection}
                disabled={collection.feature_count === 0}
              />
              <ZoomToCollectionButton
                collection={collection}
                disabled={collection.feature_count === 0}
              />
              <MoreActionsMenu
                collection={collection}
                hasProducts={productsCount > 0}
                target="collection"
              />
            </Stack>
          ) : null
        }
      />
      <Stack
        sx={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 1,
        }}
      >
        <FilterTextField
          variant="outlined"
          fullWidth
          size="small"
          type="search"
          placeholder="Search by feature name..."
          value={featureSearchValue}
          onChange={value => setFeatureSearchValue(value)}
          component={Paper}
          elevation={0}
          onFocus={() => setShowSearch(true)}
          sx={{
            '& .MuiOutlinedInput-notchedOutline': {
              border: !showSearch ? 0 : '1px solid',
            },
          }}
        />
        {showQuotaMenu && !showSearch ? (
          <>
            <Divider orientation="vertical" variant="fullWidth" flexItem />
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => {
                trackEvent(EVENT_NAME, {
                  action: `Opened manage quota modal`,
                  collectionId,
                  featureCount: collection.feature_count,
                });
                setModal(MODALS.QUOTA_MANAGER);
              }}
              sx={{flexShrink: 0, marginLeft: 1}}
              disabled={collection?.feature_count === 0}
            >
              Reserve Quota
            </Button>
          </>
        ) : showSearch ? (
          <IconButton
            onClick={() => setShowSearch(!showSearch)}
            aria-label="close search"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </Stack>

      <Divider orientation="horizontal" />

      {collection && (
        <FeatureTable
          collection={collection}
          searchString={debouncedSearchFilter}
          reservations={reservationsData?.reservations}
        />
      )}

      <Suspense>
        <QuotaManagerModal
          open={modal === MODALS.QUOTA_MANAGER}
          onClose={closeModal}
          collection={collection}
        />
      </Suspense>
    </>
  );
};

export default FeaturesPanel;
